import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  FC,
} from 'react';
import { useItinerariesWithPriceResponseQuery } from 'clientState/providers/ItinerariesPricesProvider';
import useGetBuyPathDeal from 'clientState/hooks/useGetBuyPathDeal';
import {
  SIMPLIFIED_RESULTS_META,
  SORTING_PROVIDER_MOBILE,
} from 'clientState/ab/aliases';
import { MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON } from '@components/Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';
import { useFlow, createFeature } from '@edreamsodigeo/frontend-core';
import { useSearchQuery } from '../../clientState/providers/SearchRequestProvider';
import { useSelectedUnbundleDepartureId } from 'clientState/providers/SelectedUnbundleDepartureProvider';
import { useUnbundle } from 'clientState/providers/SearchResponseProvider/SearchItinerariesResponseProvider';

const DEFAULT_SORTING = 'recommended';

const SORTING_LABELS = {
  recommended: 'recom',
  cheapest: 'price',
  fastest: 'duration',
  primeDay: 'primeDay',
  earliest: 'earliest',
};

export type SortingRequestContext = {
  sorting: string;
  firstSorting: string;
  setSorting: React.Dispatch<React.SetStateAction<string>>;
};

type SortingProviderBaseBaseProps = {
  children: React.ReactNode;
  considerMetaSelection?: boolean;
};

const SortingRequestContext = createContext<SortingRequestContext>({
  sorting: DEFAULT_SORTING,
  firstSorting: DEFAULT_SORTING,
  setSorting: () => {},
});

export const useSorting = () => useContext(SortingRequestContext);

const DefaultSortingProvider: FC = ({ children }) => {
  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [firstSorting] = useState(DEFAULT_SORTING);
  const selectedUnbundleDepartureId = useSelectedUnbundleDepartureId();

  const value = useMemo(
    () => ({
      sorting: selectedUnbundleDepartureId ? 'cheapest' : sorting,
      firstSorting,
      setSorting,
    }),
    [sorting, firstSorting, setSorting, selectedUnbundleDepartureId]
  );

  return (
    <SortingRequestContext.Provider value={value}>
      {children}
    </SortingRequestContext.Provider>
  );
};

const SortingProviderBase: FC<SortingProviderBaseBaseProps> = ({
  children,
  considerMetaSelection = false,
}) => {
  const dealBuyPath = useGetBuyPathDeal();
  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [firstSorting, setFirstSorting] = useState(DEFAULT_SORTING);
  const flow = useFlow();
  const searchRequest = useSearchQuery();
  const isMeta = searchRequest?.itinerary?.externalSelection;

  useEffect(() => {
    if (considerMetaSelection && isMeta && flow === 'desktop') {
      setSorting('cheapest');
      setFirstSorting('cheapest');
    }
  }, [considerMetaSelection, isMeta, flow]);

  useEffect(() => {
    if (dealBuyPath && flow === 'desktop') {
      setSorting('cheapest');
      setFirstSorting('cheapest');
    } else if (dealBuyPath === '') {
      setFirstSorting(DEFAULT_SORTING);
    }
  }, [dealBuyPath, flow]);

  const value = useMemo(
    () => ({
      sorting,
      firstSorting,
      setSorting,
    }),
    [sorting, firstSorting, setSorting]
  );

  return (
    <SortingRequestContext.Provider value={value}>
      {children}
    </SortingRequestContext.Provider>
  );
};

const SortingProvider = createFeature({
  name: SIMPLIFIED_RESULTS_META,
  variations: [
    (props: SortingProviderBaseBaseProps) => <SortingProviderBase {...props} />,
    (props: SortingProviderBaseBaseProps) => (
      <SortingProviderBase {...props} considerMetaSelection={true} />
    ),
    (props: SortingProviderBaseBaseProps) => (
      <SortingProviderBase {...props} considerMetaSelection={true} />
    ),
  ],
  version: 1,
});

const DefaultOrCheapestSortingProviderMobile: FC = ({ children }) => {
  const dealBuyPath = useGetBuyPathDeal();
  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [firstSorting, setFirstSorting] = useState(DEFAULT_SORTING);
  const flow = useFlow();

  useEffect(() => {
    if (dealBuyPath && flow === 'mobile') {
      setSorting('cheapest');
      setFirstSorting('cheapest');
    } else if (dealBuyPath === '') {
      setFirstSorting(DEFAULT_SORTING);
    }
  }, [dealBuyPath, flow]);

  const value = useMemo(
    () => ({
      sorting,
      firstSorting,
      setSorting,
    }),
    [sorting, firstSorting, setSorting]
  );

  return (
    <SortingRequestContext.Provider value={value}>
      {children}
    </SortingRequestContext.Provider>
  );
};

export const SortingProviderMobile = createFeature({
  name: SORTING_PROVIDER_MOBILE,
  variations: [
    DefaultSortingProvider,
    DefaultSortingProvider,
    DefaultOrCheapestSortingProviderMobile,
  ],
  version: 1,
});

export const useSortingTypeLabel = () => {
  const { sorting: sortingType } = useSorting();
  return SORTING_LABELS[sortingType];
};

const getPriceAmount = (number) => {
  const couponPrice = number?.fees?.filter(
    (fee) => fee.type.id === MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON
  );
  return couponPrice?.length > 0
    ? Number(couponPrice[0].price?.amount)
    : number?.price?.amount;
};

export const priceComparator = (a, b) => {
  return getPriceAmount(a) - getPriceAmount(b);
};
export const recommendedComparator = (a, b) =>
  b.meRating - a.meRating || priceComparator(a, b);
export const durationComparator = (a, b) =>
  a.duration - b.duration || priceComparator(a, b);

export const primeDayDealComparator = (a, b) => {
  const aPrimeDayFare = a.campaignConfig?.primeDayConfig?.isPrimeDayFare
    ? 1
    : 0;
  const bPrimeDayFare = b.campaignConfig?.primeDayConfig?.isPrimeDayFare
    ? 1
    : 0;
  return bPrimeDayFare - aPrimeDayFare || priceComparator(a, b);
};
export const dateComparator = (a, b): number => {
  const aDate = new Date(a.legs[0].segments[0].departureDate).valueOf();
  const bDate = new Date(b.legs[0].segments[0].departureDate).valueOf();
  return aDate - bDate || priceComparator(a, b) || durationComparator(a, b);
};

const sortBy = {
  cheapest: priceComparator,
  recommended: recommendedComparator,
  fastest: durationComparator,
  primeDay: primeDayDealComparator,
  earliest: dateComparator,
};

const useSortedItineraries = (type, search) => {
  const [itineraryLists, setItineraryLists] = useState({});
  const [isUnbundle] = useUnbundle();
  const itineraries = search?.itineraries;
  const cachedItinerariesDefault = itineraryLists[type];
  const cachedItinerariesUnbundle =
    itineraries?.length === itineraryLists[type]?.length
      ? itineraryLists[type]
      : null;
  const cachedItineraries = isUnbundle
    ? cachedItinerariesUnbundle
    : cachedItinerariesDefault;
  const result =
    itineraries && (cachedItineraries || [...itineraries].sort(sortBy[type]));

  useEffect(() => {
    setItineraryLists({});
  }, [search, setItineraryLists]);

  useEffect(() => {
    if (result && !cachedItineraries) {
      setItineraryLists({
        ...itineraryLists,
        [type]: result,
      });
    }
  }, [setItineraryLists, itineraryLists, type, result, cachedItineraries]);

  return result;
};

export type SortedItineraryContext = {
  loading: boolean;
  data: any;
  error: any;
};
const LOADING_STATE = {
  loading: true,
  data: null,
  error: null,
};

const SortedItinerariesContext =
  createContext<SortedItineraryContext>(LOADING_STATE);

export const useSortedItinerariesQuery = () =>
  useContext(SortedItinerariesContext);

export const SortedItinerariesProvider: FC = ({ children }) => {
  const { sorting: type } = useSorting();
  const searchQueryState = useItinerariesWithPriceResponseQuery();
  const { loading, error, data } = searchQueryState;
  const itineraries = useSortedItineraries(type, data);

  const value = useMemo(
    () =>
      loading || error || !itineraries
        ? {
            loading: loading || !itineraries,
            error,
            data: null,
          }
        : {
            loading: false,
            error: null,
            data: {
              ...data,
              itineraries,
            },
          },
    [loading, error, data, itineraries]
  );

  return (
    <SortedItinerariesContext.Provider value={value}>
      {children}
    </SortedItinerariesContext.Provider>
  );
};

export default SortingProvider;
